// ============================================================================
// BoardActionCard — a board action Ask AI drafted (item #737; TCSD-0416).
//
// Drawn to the 7 Oct demo's card: "Drafted · not done", one line saying what
// will happen, Do it / Discard. Nothing is written until Do it, and then it is
// the same write the command center's button makes. Ben may change who builds
// it, the reason, the priority or his words before pressing — the same choices
// the button's own pane offers, nothing more.
// ============================================================================
import { useState } from 'react';
import { LayoutList } from 'lucide-react';
import type { CopilotProposalBase } from '../../../shared/types';
import { CardShell } from './card-shell';
import { BOARD_ACTION_TITLES, boardActionLine, type BoardActionInput } from '../board-action';
import { PUSHBACK_REASONS, PRIORITY_WORDS } from '../../../../supabase/functions/build-room-bridge/handler';

interface BoardActionCardProps {
  proposal: CopilotProposalBase;
  isCommitting: boolean;
  onDoIt: (edited_input: Record<string, unknown>, was_edited: boolean) => void;
  onDiscard: () => void;
}

const field = 'w-full rounded-md border border-tcs-border bg-tcs-bg-muted px-2 py-1.5 text-[13px] text-tcs-text';

export function BoardActionCard({ proposal, isCommitting, onDoIt, onDiscard }: BoardActionCardProps) {
  const initial = proposal.input as unknown as BoardActionInput;
  const [draft, setDraft] = useState<BoardActionInput>(initial);
  const set = (patch: Partial<BoardActionInput>) => setDraft((d) => ({ ...d, ...patch }));
  const edited = JSON.stringify(draft) !== JSON.stringify(initial);
  const open = proposal.status === 'Proposed';
  const needsWords = draft.action === 'approve' && draft.choice === 'own' && !(draft.words ?? '').trim();

  return (
    <CardShell
      title={BOARD_ACTION_TITLES[draft.action] ?? 'Board action'}
      icon={<LayoutList className="h-4 w-4" />}
      status={proposal.status}
      committedLabel={proposal.target_row_id ?? 'Written to the board'}
      isCommitting={isCommitting}
      doItDisabled={needsWords}
      doItDisabledReason={needsWords ? 'Say which option, in your words.' : undefined}
      onDoIt={() => onDoIt(draft as unknown as Record<string, unknown>, edited)}
      onDiscard={onDiscard}
    >
      <div className="text-[10.5px] font-semibold uppercase tracking-[0.06em] text-tcs-text-muted">
        {open ? 'Drafted · not done' : proposal.status === 'Discarded' ? 'Discarded · nothing was written' : 'Done · signed by you'}
      </div>
      <p className="text-[13px] text-tcs-text">{boardActionLine(draft)}</p>

      {open && draft.action === 'approve' && (
        <div className="space-y-1 text-[13px] text-tcs-text">
          <div className="text-[10.5px] font-semibold uppercase tracking-[0.06em] text-tcs-text-muted">Who builds it</div>
          <label className="flex items-start gap-2">
            <input type="radio" name={`rt-${proposal.action_id}`} checked={draft.route !== 'builder'} onChange={() => set({ route: 'cowork' })} className="mt-1" />
            <span><b>I'll run it in Cowork</b>: a prompt is written and you run it.</span>
          </label>
          <label className="flex items-start gap-2">
            <input type="radio" name={`rt-${proposal.action_id}`} checked={draft.route === 'builder'} onChange={() => set({ route: 'builder' })} className="mt-1" />
            <span><b>The Builder builds it</b>: it starts as soon as you approve, and its pull request comes to you to merge.</span>
          </label>
          {draft.build_route && <p className="text-[12px] text-tcs-text-muted">The write-up says: {draft.build_route.split('\n')[0]}</p>}
        </div>
      )}

      {open && draft.action === 'pushback' && (
        <select aria-label="Why" className={field} value={draft.reason ?? ''} onChange={(e) => set({ reason: e.target.value })}>
          {Object.entries(PUSHBACK_REASONS).map(([k, label]) => (
            <option key={k} value={k}>
              {label}
            </option>
          ))}
        </select>
      )}

      {open && draft.action === 'priority' && (
        <select aria-label="Priority" className={field} value={draft.to ?? ''} onChange={(e) => set({ to: e.target.value })}>
          {Object.entries(PRIORITY_WORDS).map(([k, label]) => (
            <option key={k} value={k}>
              {label}
            </option>
          ))}
        </select>
      )}

      {open && ['approve', 'pushback', 'park', 'unpark'].includes(draft.action) && (
        <textarea
          aria-label="Your words"
          rows={2}
          className={field}
          value={draft.words ?? ''}
          placeholder={draft.action === 'approve' && draft.choice === 'own' ? 'The option you mean (required)' : 'Your words (optional)'}
          onChange={(e) => set({ words: e.target.value })}
        />
      )}
    </CardShell>
  );
}
